import {
    MODAL_TITLE,
    MODAL_CANCEL_LABEL,
    EMPTY_MESSAGE,
    EMPTY_HINT,
    formatPeriodWithDays,
    groupPeriodsByYear,
    calculateYearTotal,
    calculateTotalDays,
} from './vacationPaidRestHistoryModal.definitions';
import type { PaidRestRow } from '../../models/vacationPaidRest.types';

export interface VacationPaidRestHistoryPanelProps {
    row: PaidRestRow;
    titleId: string;
    onClose: () => void;
}

export function VacationPaidRestHistoryPanel({ row, titleId, onClose }: VacationPaidRestHistoryPanelProps) {
    const periods = row.paidRestPeriods ?? [];
    const groupedByYear = groupPeriodsByYear(periods);
    const totalDays = calculateTotalDays(periods);
    const currentYear = new Date().getFullYear();
    const yearTotal = calculateYearTotal(periods, currentYear);

    const hasPeriods = periods.length > 0;

    return (
        <>
            <div className="border-b border-raro-gray-200 px-6 py-5 flex-shrink-0">
                <div className="flex items-start justify-between">
                    <div>
                        <h2 id={titleId} className="text-lg font-semibold text-raro-gray-900 font-montserrat">
                            {MODAL_TITLE}
                        </h2>
                        <p className="mt-1 text-sm text-raro-gray-600 font-montserrat">
                            {row.collaboratorName}
                            {row.companyName && ` • ${row.companyName}`}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className="text-raro-gray-400 hover:text-raro-gray-500">
                        <span className="sr-only">Fechar</span>
                        <svg
                            className="h-6 w-6"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {hasPeriods && (
                    <div className="mt-4 flex gap-4 text-sm font-montserrat">
                        <div>
                            <span className="text-raro-gray-600">Total em {currentYear}:</span>
                            <span className="ml-2 font-semibold text-raro-gray-900">
                                {yearTotal > 0 ? `${yearTotal}d` : '—'}
                            </span>
                        </div>
                        <div>
                            <span className="text-raro-gray-600">Total geral:</span>
                            <span className="ml-2 font-semibold text-raro-gray-900">
                                {totalDays > 0 ? `${totalDays}d` : '—'}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex-grow overflow-y-auto px-6 py-5">
                {!hasPeriods ? (
                    <div className="rounded-lg border border-raro-gray-200 bg-raro-gray-50 p-6 text-center">
                        <p className="text-sm font-medium text-raro-gray-900 font-montserrat">{EMPTY_MESSAGE}</p>
                        <p className="mt-1 text-sm text-raro-gray-600 font-montserrat">{EMPTY_HINT}</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {Array.from(groupedByYear.entries())
                            .sort(([yearA], [yearB]) => yearB - yearA)
                            .map(([year, yearPeriods]) => (
                                <div key={year}>
                                    <h3 className="text-sm font-semibold text-raro-gray-900 font-montserrat mb-3">{year}</h3>
                                    <div className="space-y-2">
                                        {yearPeriods.map((period) => {
                                            const { formatted, days } = formatPeriodWithDays(period.startDate, period.endDate);
                                            return (
                                                <div
                                                    key={`${period.startDate}-${period.endDate}`}
                                                    className="rounded-lg border border-raro-gray-100 p-3 bg-white hover:bg-raro-gray-50 transition-colors"
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div>
                                                            <p className="text-sm font-medium text-raro-gray-900 font-montserrat">{formatted}</p>
                                                            {period.observations && (
                                                                <p className="mt-1 text-xs text-raro-gray-600 font-montserrat">
                                                                    {period.observations}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <span className="text-sm font-semibold text-raro-gray-900 font-montserrat whitespace-nowrap ml-4">
                                                            {days}d
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                    </div>
                )}
            </div>

            <div className="border-t border-raro-gray-200 bg-raro-gray-50 px-6 py-4 flex-shrink-0 flex justify-end">
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded-xl border border-raro-gray-200 bg-white px-4 py-2 text-sm font-semibold text-raro-gray-700 transition-colors hover:bg-raro-gray-50 font-montserrat"
                >
                    {MODAL_CANCEL_LABEL}
                </button>
            </div>
        </>
    );
}

export default VacationPaidRestHistoryPanel;
